import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronDown,
  MessageCircle,
  Phone,
  Link
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

// FAQ Data
const faqData = [
  // General FAQs
  {
    category: "general",
    question: "How do I book a service?",
    answer: "Simply browse our services, select what you need, choose your preferred time slot, and confirm your booking. You'll receive instant confirmation via SMS and email, and can track your service provider in real-time through our app."
  },
  {
    category: "general",
    question: "Are your service providers verified?",
    answer: "Absolutely! All our service providers undergo comprehensive background checks, skills verification, identity verification, and professional training before joining our platform. We also maintain ongoing quality assessments to ensure consistent service standards."
  },
  {
    category: "general",
    question: "Do you provide services on weekends and holidays?",
    answer: "Yes! We provide services 7 days a week, including weekends and most holidays. Holiday bookings may have premium pricing. Check our app for real-time availability and special holiday schedules."
  },
  {
    category: "general",
    question: "What areas do you service?",
    answer: "We currently provide services in Joginder Nagar and surrounding areas within Himachal Pradesh. Enter your pincode on our website or app to check service availability in your area."
  },
  {
    category: "general",
    question: "Do you provide emergency services?",
    answer: "Yes, we offer 24/7 emergency services for urgent matters like plumbing issues, electrical problems, and other critical home services. Emergency services may have additional charges."
  },

  // Booking FAQs
  {
    category: "booking",
    question: "Can I reschedule or cancel my booking?",
    answer: "Yes, you can reschedule or cancel your booking up to 2 hours before the scheduled time through our app or website. For cancellations made less than 2 hours in advance, a minimal cancellation fee may apply."
  },
  {
    category: "booking",
    question: "How far in advance can I book a service?",
    answer: "You can book services up to 30 days in advance. For same-day bookings, we recommend booking at least 2-3 hours ahead, though emergency services are available 24/7 with additional charges."
  },
  {
    category: "booking",
    question: "Can I request a specific service provider?",
    answer: "Yes, if you've had a good experience with a particular service provider, you can request them for future bookings. However, availability may vary based on their schedule."
  },
  {
    category: "booking",
    question: "What is the minimum booking duration?",
    answer: "Minimum booking duration varies by service type. Most services have a minimum duration of 1 hour, while some specialized services might require longer minimums. Check individual service pages for specific details."
  },
  {
    category: "booking",
    question: "Can I book multiple services for the same time?",
    answer: "Yes, you can book multiple services. Our system will coordinate to ensure all service providers arrive as scheduled. You can also opt for our home service packages for better value."
  },

  // Payment FAQs
  {
    category: "payment",
    question: "What payment methods do you accept?",
    answer: "We accept all major payment methods including credit/debit cards, UPI, net banking, digital wallets (Paytm, PhonePe, Google Pay), and cash payments. All online transactions are secured with 256-bit SSL encryption."
  },
  {
    category: "payment",
    question: "When will I be charged for the service?",
    answer: "For standard services, payment is processed after service completion. For certain services or packages, a partial advance payment may be required at the time of booking."
  },
  {
    category: "payment",
    question: "Do you offer any discounts or promotions?",
    answer: "Yes, we regularly offer seasonal discounts, first-time user offers, and special promotions. Subscribe to our newsletter and enable notifications to stay updated about ongoing offers."
  },
  {
    category: "payment",
    question: "Is there a cancellation fee?",
    answer: "Cancellations made more than 2 hours before the scheduled service time are free. Late cancellations (within 2 hours) or no-shows may incur a cancellation fee of up to 25% of the service value."
  },
  {
    category: "payment",
    question: "Do you provide invoices for services?",
    answer: "Yes, you'll receive a detailed digital invoice for all services via email. You can also access your service history and invoices through your account dashboard."
  },

  // Support FAQs
  {
    category: "support",
    question: "What if I'm not satisfied with the service?",
    answer: "We offer a 100% satisfaction guarantee. If you're not completely happy with the service, contact us within 24 hours and we'll make it right - either by re-doing the work at no extra cost or providing a full refund. Your satisfaction is our priority."
  },
  {
    category: "support",
    question: "How can I track my service provider?",
    answer: "Once your booking is confirmed, you'll receive real-time updates via SMS and push notifications. You can track your service provider's location, estimated arrival time, and contact them directly through our secure platform."
  },
  {
    category: "support",
    question: "What if something gets damaged during service?",
    answer: "All our services are insured, and we take full responsibility for any damages that might occur during service delivery. Simply report the incident to our customer support within 24 hours, and we'll handle the repairs or compensation."
  },
  {
    category: "support",
    question: "How can I provide feedback?",
    answer: "You can rate and review your service experience directly through our app or website after service completion. For detailed feedback or concerns, you can contact our support team 24/7."
  },
  {
    category: "support",
    question: "Is there a warranty on services?",
    answer: "Yes, most of our services come with a service warranty. The warranty period varies by service type, ranging from 7 days to 6 months. Check the service details page for specific warranty information."
  }
];

export default function FAQPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // Filter FAQs based on active category
  const filteredFAQs = activeTab === 'all' 
    ? faqData 
    : faqData.filter(faq => faq.category === activeTab);

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 text-white py-12 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 md:top-20 left-10 md:left-20 w-20 md:w-32 h-20 md:h-32 bg-white opacity-10 rounded-full"></div>
          <div className="absolute bottom-10 md:bottom-20 right-10 md:right-20 w-32 md:w-48 h-32 md:h-48 bg-white opacity-5 rounded-full"></div>
          <div className="absolute top-20 md:top-40 right-20 md:right-40 w-12 md:w-20 h-12 md:h-20 bg-white opacity-15 rounded-full"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="text-center mb-6 md:mb-12">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-3xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-8 bg-gradient-to-r from-white to-primary-100 bg-clip-text text-transparent"
            >
              Frequently Asked Questions
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-sm md:text-xl lg:text-2xl text-primary-100 max-w-4xl mx-auto leading-relaxed px-4"
            >
              Find answers to common questions about our services.
              <br className="hidden md:block" />
              Can't find what you're looking for? We're here to help!
            </motion.p>
          </div>
        </div>
      </div>

      {/* Main FAQ Section */}
      <div className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* FAQ Categories */}
          <div className="mb-12 flex flex-wrap justify-center gap-4">
            <motion.button
              key="all"
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeTab === 'all' 
                  ? 'bg-primary-600 text-white shadow-lg' 
                  : 'bg-gray-100 text-gray-600 hover:bg-primary-50 hover:text-primary-600'
              }`}
              onClick={() => setActiveTab('all')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              All Questions
            </motion.button>
            {['General', 'Booking', 'Payment', 'Support'].map((category) => (
              <motion.button
                key={category}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeTab === category.toLowerCase() 
                    ? 'bg-primary-600 text-white shadow-lg' 
                    : 'bg-gray-100 text-gray-600 hover:bg-primary-50 hover:text-primary-600'
                }`}
                onClick={() => setActiveTab(category.toLowerCase())}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category}
              </motion.button>
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredFAQs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full text-left p-6 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-2xl"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    <motion.div
                      animate={{ rotate: openFAQ === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex-shrink-0"
                    >
                      <ChevronDown className="h-6 w-6 text-primary-600" />
                    </motion.div>
                  </div>
                </button>
                
                <motion.div
                  initial={false}
                  animate={{ height: openFAQ === index ? 'auto' : 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6">
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* Contact Support Section */}
          <motion.div 
            className="mt-16 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-8 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Still have questions?</h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Our friendly support team is here to help you 24/7. Get in touch and we'll get back to you as soon as possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
              >
                <MessageCircle className="h-5 w-5" />
                <span>Live Chat</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border border-primary-600 text-primary-600 px-8 py-3 rounded-xl font-semibold hover:bg-primary-50 transition-colors flex items-center justify-center space-x-2"
              >
                <Phone className="h-5 w-5" />
                <span>Call Support</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Additional Resources */}
          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Need More Help?</h3>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <RouterLink 
                to="/contact" 
                className="flex items-center justify-center space-x-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                <span>Contact Us</span>
                <Link className="h-5 w-5" />
              </RouterLink>
              <RouterLink 
                to="/services" 
                className="flex items-center justify-center space-x-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                <span>Browse Services</span>
                <Link className="h-5 w-5" />
              </RouterLink>
              <RouterLink 
                to="/about" 
                className="flex items-center justify-center space-x-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                <span>About Us</span>
                <Link className="h-5 w-5" />
              </RouterLink>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
